import { ResponseType } from 'axios';

import { getExternal } from 'src/utils/getExternalContents';

import {
  FindPwParams,
  FindIdParams,
  ExistsSocial,
  ReqCheckFindPw,
  RegisterParams,
  ExistsIdParams,
  TermsDateParams,
  FindByInfoParams,
  CustomTokenParams,
  SearchStoreParams,
  ChangeFindPwParams,
  ReqRestoreDormancy,
  ConfirmFindIdParams,
  CommonGuestApiParams,
  ResponsibilityParams,
  AuthWayResponsibility,
  AuthResponsibilityParams,
} from 'src/types/guest';

import axiosInstance from './instance';

const getBrandCode = () => localStorage.getItem('brandCode') ?? '';

const guestApi = {
  // 약관 조회(Terms inquiry)
  // https://dev.clp.kr/spc/api/static/docs/guest/guest_account.html#resources-guest-brand-terms
  // no res
  terms(params?: CommonGuestApiParams) {
    return axiosInstance.get(`/guest/${params?.brandCode ?? getBrandCode()}/brand/terms`);
  },

  // 소셜 계정 custom Token 조회(social auth custom token)
  // https://dev.clp.kr/spc/api/static/docs/guest/guest_account.html#resources-social-custom-token
  // no res
  customToken(params: CustomTokenParams) {
    return axiosInstance.get(`/guest/social/customToken`, { params });
  },

  // 매장 검색(get store search list)
  // https://dev.clp.kr/spc/api/static/docs/guest/guest_account.html#resources-guest-store-fetch-page
  // no res
  searchStore(params: SearchStoreParams) {
    return axiosInstance.get(`/guest/${params?.brandCode ?? getBrandCode()}/store`, { params });
  },

  // 사용자 인증 요청(User membership verification request)
  // https://dev.clp.kr/spc/api/static/docs/guest/guest_account.html#resources-guest-register-auth-brand-request-responsibility
  // no res
  authResponsibility(params: AuthResponsibilityParams) {
    return axiosInstance.post(
      `/guest/${params?.brandCode ?? getBrandCode()}/auth/responsibility/${params.responsibility}`,
      { ...params }
    );
  },

  // 약관 발효일 조회(Term Date inquiry)
  // https://dev.clp.kr/spc/api/static/docs/guest/guest_account.html#resources-guest-brand-term-date
  // no res
  termsDate(params: TermsDateParams) {
    return axiosInstance.get(`/guest/${params?.brandCode ?? getBrandCode()}/brand/termDate`, {
      params,
    });
  },

  // 직무 목록 조회(get duty list)
  // https://clpapi.tinkeredu.co.kr/static/docs/guest.html#resources-guest-duty
  // no res
  duty(params?: CommonGuestApiParams) {
    return axiosInstance.get(`/guest/${params?.brandCode ?? getBrandCode()}/duty/combo`);
  },

  // 회원가입(Request registration)
  // https://dev.clp.kr/spc/api/static/docs/guest/guest_account.html#resources-guest-register-final
  register(params: RegisterParams) {
    return axiosInstance.post(
      `/guest/${params?.brandCode ?? getBrandCode()}/member/register`,
      params
    );
  },

  // 직책 목록 조회(get responsibility list)
  // https://clpapi.tinkeredu.co.kr/static/docs/guest.html#resources-guest-responsibility
  // no res
  responsibility(params?: ResponsibilityParams) {
    return axiosInstance.get(`/guest/${params?.brandCode ?? getBrandCode()}/responsibility/combo`, {
      params,
    });
  },

  // 브랜드 담당자(Brand manager)
  // https://dev.clp.kr/spc/api/static/docs/guest/guest_account.html#resources-guest-brand-manager
  // no res
  managerDetail(params?: CommonGuestApiParams) {
    return axiosInstance.get(`/guest/${params?.brandCode ?? getBrandCode()}/brand/manager`);
  },

  //  아이디 중복 확인(ID duplication check)
  // https://dev.clp.kr/spc/api/static/docs/guest/guest_account.html#resources-guest-exists-id
  // no res
  existsId(params: ExistsIdParams) {
    return axiosInstance.get(`/guest/${params?.brandCode ?? getBrandCode()}/member/exists/id`, {
      params,
    });
  },

  // 소셜 중복 확인(Social duplication check)
  // https://dev.clp.kr/spc/api/static/docs/guest/guest_account.html#resources-guest-exists-social
  // no res
  existsSocial(params: ExistsSocial) {
    return axiosInstance.get(`/guest/${params?.brandCode ?? getBrandCode()}/member/exists/social`, {
      params,
    });
  },

  //  계정 찾기 정보(Information used to find an account)
  // https://dev.clp.kr/spc/api/static/docs/guest/guest_account.html#resources-guest-find-info
  externalFind(params?: CommonGuestApiParams) {
    return axiosInstance.get(`/guest/find`, { params });
  },

  // 정보로 계정 찾기(Find an account with information)
  // https://dev.clp.kr/spc/api/static/docs/guest/guest_account.html#resources-guest-find-by-info
  findByInfo(params: FindByInfoParams) {
    return axiosInstance.get(`/guest/find/by/info`, { params });
  },

  // Find account id
  // https://dev.clp.kr/spc/api/static/docs/guest/guest_account.html#resources-guest-find-id
  requestFindId(params: FindIdParams) {
    return axiosInstance.post(`/guest/find/id/request`, params);
  },

  // 찾기(Confirm to find account ID)
  // https://dev.clp.kr/spc/api/static/docs/guest/guest_account.html#resources-guest-find-id-confirm
  findId(params: ConfirmFindIdParams) {
    return axiosInstance.put(`/guest/find/id`, params);
  },

  // 계정 비밀번호 변경(Change account password)
  // https://dev.clp.kr/spc/api/static/docs/guest/guest_account.html#resources-guest-change-pw-request
  requestFindPw(params: FindPwParams) {
    return axiosInstance.post(`/guest/find/pw/request`, params);
  },

  // (Optional)확인(Confirm to Change account password)
  // https://dev.clp.kr/spc/api/static/docs/guest/guest_account.html#resources-guest-change-pw-check
  checkFindPw(params: ReqCheckFindPw) {
    return axiosInstance.put(`/guest/find/pw/check`, params);
  },

  // 수정(Modify account password)
  // https://dev.clp.kr/spc/api/static/docs/guest/guest_account.html#resources-guest-change-pw-change
  changeFindPw(params: ChangeFindPwParams) {
    return axiosInstance.put(`/guest/find/pw/change`, params);
  },

  // 사용자 가입 인증 사용 여부 조회(Check whether member registration verification is used)
  // https://dev.clp.kr/spc/api/static/docs/guest/guest_account.html#resources-guest-register-way-fetch-responsibility
  authWayResponsibility(params: AuthWayResponsibility) {
    return axiosInstance.get(
      `/guest/${getBrandCode()}/auth/responsibility/${params.responsibility}/way`
    );
  },

  // 휴면 해제
  // https://dev.clp.kr/anchor/api/static/docs/front/account.html#resources-account-sign-in-idp
  restoreDormancy(params: ReqRestoreDormancy) {
    return axiosInstance.put(`/guest/membership/member/disposal/${params.id}/restore`, params);
  },

  // 언어팩(get brand language pack information)
  // https://dev.clp.kr/spc/api/static/docs/guest/guest_account.html#resources-guest-i18n-language-pack-fetch
  guestLanguagePack(params: CommonGuestApiParams) {
    return axiosInstance.get(`/guest/${params.brandCode}/i18n/language/pack`);
  },

  // 외부 URL HTML 호출(get External URL HTML)
  // API 문서 없음
  policyContents(url: string, contentsType: ResponseType, params?: object) {
    return getExternal(url, contentsType, params);
  },
  // 국가 코드(Country code)
  // https://dev.clp.kr/spc/api/static/docs/guest/guest_account.html#_2_5_1_%EA%B5%AD%EA%B0%80%EC%BD%94%EB%93%9C_%EB%AA%A9%EB%A1%9D_%EC%A1%B0%ED%9A%8C_country_code_list_lookup
  guestCountryList(params?: CommonGuestApiParams) {
    return axiosInstance.get(`/guest/${params?.brandCode ?? getBrandCode()}/i18n/country/list`);
  },

  // 직책 코드 검증
  // https://www.notion.so/Standard-API-RestDoc-22d62a268211804ab917d034dcdb2e30?source=copy_link#22d62a268211808daf43f8dee0624f4d
  fetchAuthCode(code: string) {
    return axiosInstance.get(`/guest/auth/code/${code}`);
  },
};

export default guestApi;
